import React, { useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { maskPhoneInput } from '../../utils/formatPhone';
import PROVINCES from '../../constant/provinces';

interface CreateThunderbirdOrderModalProps {
  onSubmit: (data: any) => void;
  isLoading: boolean;
  onClose?: () => void;
}

// Centred pop-up (same chrome as the order pop-ups): header, two cards of fields, and a
// footer bar with the Create button.
const CreateThunderbirdOrderModal: React.FC<
  CreateThunderbirdOrderModalProps
> = ({ onSubmit, isLoading = false, onClose }) => {
  const [formData, setFormData] = useState<any>({
    numberOfKits: 0,
    numberOfBoxes: 0,
    customerName: '',
    phoneNumber: '',
    emailAddress: '',
    shippingAddress: {
      name: '',
      businessName: '',
      streetAddress: '',
      unitSuite: '',
      city: '',
      province: '',
      postalCode: '',
    },
  });

  // "Deliver to" follows the customer name until someone types a different recipient;
  // clearing it hands it back. Staff rarely ship to anyone but the customer.
  const [recipientEdited, setRecipientEdited] = useState(false);

  // The phone shows as "(416) 555-0100" while typed; the API gets the digits (and any
  // extension as typed). Anything that is not a North American number passes through as is.
  const [phoneDisplay, setPhoneDisplay] = useState('');
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, display } = maskPhoneInput(e.target.value, phoneDisplay);
    setPhoneDisplay(display);
    setFormData((prev: any) => ({ ...prev, phoneNumber: value }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    setFormData((prev: any) => ({
      ...prev,
      [name]: type === 'number' ? Number(value) : value,
      ...(name === 'customerName' && !recipientEdited
        ? { shippingAddress: { ...prev.shippingAddress, name: value } }
        : {}),
    }));
  };

  const handleShippingChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    if (name === 'name') setRecipientEdited(value.trim().length > 0);
    setFormData((prev: any) => ({
      ...prev,
      shippingAddress: { ...prev.shippingAddress, [name]: value },
    }));
  };

  // The API accepts anything, so the form holds the line: an order needs a customer and
  // at least one kit or box. Everything else can be filled in later from the order.
  const canSubmit =
    formData.customerName.trim().length > 0 &&
    (formData.numberOfKits > 0 || formData.numberOfBoxes > 0);
  const blockedReason = canSubmit
    ? undefined
    : 'Enter a customer name and at least one kit or box';

  const handleSubmit = (e?: React.SyntheticEvent) => {
    e?.preventDefault();
    if (isLoading || !canSubmit) return;
    onSubmit(formData);
  };

  const text = (
    id: string,
    name: string,
    label: string,
    value: string,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
    span2 = false,
  ) => (
    <form
      className={`input-group${span2 ? ' op-span-2' : ''}`}
      onSubmit={handleSubmit}
      autoComplete="off"
    >
      <label htmlFor={id}>{label}</label>
      <input
        autoComplete="off"
        type="text"
        id={id}
        name={name}
        value={value}
        onChange={onChange}
      />
    </form>
  );

  return (
    // One <form> per field, on purpose. Chrome classifies address fields (and then offers
    // to fill and to save them) only in forms with three or more text fields, and it ignores
    // autocomplete="off" for addresses. Every mini form submits the whole order on Enter.
    <div className="op">
      <div className="op-head">
        <div>
          <h4>Create Thunderbird order</h4>
          <div className="op-meta">
            Enter the customer and shipping details. The order lands in New
            orders like any other.
          </div>
        </div>
        <div className="op-head-actions">
          {onClose ? (
            <button
              type="button"
              className="op-close"
              aria-label="Close (Esc)"
              title="Close (Esc)"
              onClick={onClose}
            >
              ×
            </button>
          ) : null}
        </div>
      </div>

      <div className="op-body">
        <div className="op-grid op-grid--equal" style={{ marginTop: 16 }}>
          <div className="op-col">
            <section className="op-card">
              <div className="op-card-head">
                <h5>Customer</h5>
              </div>
              <div className="op-card-body">
                <div className="op-form-grid">
                  {text(
                    'customerName',
                    'customerName',
                    'Customer name',
                    formData.customerName,
                    handleChange,
                    true,
                  )}
                  <form
                    className="input-group"
                    onSubmit={handleSubmit}
                    autoComplete="off"
                  >
                    <label htmlFor="phoneNumber">Phone</label>
                    <input
                      autoComplete="off"
                      type="tel"
                      inputMode="tel"
                      id="phoneNumber"
                      name="phoneNumber"
                      value={phoneDisplay}
                      onChange={handlePhoneChange}
                    />
                  </form>
                  {text(
                    'emailAddress',
                    'emailAddress',
                    'Email',
                    formData.emailAddress,
                    handleChange,
                  )}
                </div>
              </div>
            </section>

            <section className="op-card">
              <div className="op-card-head">
                <h5>Items</h5>
              </div>
              <div className="op-card-body">
                <div className="op-form-grid">
                  <form
                    className="input-group"
                    onSubmit={handleSubmit}
                    autoComplete="off"
                  >
                    <label htmlFor="numberOfKits">Kits</label>
                    <input
                      type="number"
                      min="0"
                      id="numberOfKits"
                      name="numberOfKits"
                      value={formData.numberOfKits || ''}
                      placeholder="0"
                      onChange={handleChange}
                    />
                  </form>
                  <form
                    className="input-group"
                    onSubmit={handleSubmit}
                    autoComplete="off"
                  >
                    <label htmlFor="numberOfBoxes">Boxes</label>
                    <input
                      type="number"
                      min="0"
                      id="numberOfBoxes"
                      name="numberOfBoxes"
                      value={formData.numberOfBoxes || ''}
                      placeholder="0"
                      onChange={handleChange}
                    />
                  </form>
                </div>
              </div>
            </section>
          </div>

          <div className="op-col">
            <section className="op-card">
              <div className="op-card-head">
                <h5>Shipping address</h5>
              </div>
              <div className="op-card-body">
                <div className="op-form-grid">
                  {text(
                    'shipping-name',
                    'name',
                    'Deliver to',
                    formData.shippingAddress.name,
                    handleShippingChange,
                  )}
                  {text(
                    'shipping-businessName',
                    'businessName',
                    'Business or organisation',
                    formData.shippingAddress.businessName,
                    handleShippingChange,
                  )}
                  {text(
                    'shipping-streetAddress',
                    'streetAddress',
                    'Street address',
                    formData.shippingAddress.streetAddress,
                    handleShippingChange,
                    true,
                  )}
                  {text(
                    'shipping-unitSuite',
                    'unitSuite',
                    'Unit / suite',
                    formData.shippingAddress.unitSuite,
                    handleShippingChange,
                  )}
                  {text(
                    'shipping-city',
                    'city',
                    'City',
                    formData.shippingAddress.city,
                    handleShippingChange,
                  )}
                  <form
                    className="input-group"
                    onSubmit={handleSubmit}
                    autoComplete="off"
                  >
                    <label htmlFor="shipping-province">Province</label>
                    <select
                      className="select-field"
                      id="shipping-province"
                      name="province"
                      value={formData.shippingAddress.province}
                      onChange={handleShippingChange}
                    >
                      <option value="">Select a province</option>
                      {PROVINCES.map((province) => (
                        <option key={province} value={province}>
                          {province}
                        </option>
                      ))}
                    </select>
                  </form>
                  {text(
                    'shipping-postalCode',
                    'postalCode',
                    'Postal code',
                    formData.shippingAddress.postalCode,
                    handleShippingChange,
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>

      <div className="op-foot">
        <button
          type="button"
          onClick={handleSubmit}
          className="btn-app btn-app--primary op-next"
          disabled={isLoading || !canSubmit}
          title={blockedReason}
        >
          {isLoading ? <Spinner size="sm" /> : 'Create order'}
        </button>
      </div>
    </div>
  );
};

export default CreateThunderbirdOrderModal;
